import React, { useEffect } from 'react';
import { sendCustomTrackingInfo } from '@panel/commons';
import styled, { css } from 'styled-components';
import alertCircleIcon from '@panel/commons/assets/icons/AlertCircle.svg';
import blueAlertCircleIcon from '@panel/commons/assets/icons/AlertCircleBlue.svg';
import brownAlertCircleIcon from '@panel/commons/assets/icons/AlertCircleBrown.svg';
import { select } from '@panel/commons/core/themev2';
import { selectColor } from '@panel/commons/core';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

const Alert = ({
  children,
  buttonText,
  onClose,
  isMobile,
  isNeutral,
  isWarning,
  optionalButtonText,
  onOptionalButtonPress,
  dataLayerText,
  optionalButtonDataPanel,
  buttonDataPanel,
  optionalButtonDataLayer,
  fontSize,
  setPosition,
  setTop,
  setLeft,
  isFromCRM
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (dataLayerText) {
      sendCustomTrackingInfo({
        event: 'trackEvent',
        eventCategory: 'Panel',
        eventAction: 'Mis avisos',
        eventLabel: `Mensaje de advertencia-${dataLayerText}_Impresion`
      });
    }
  }, [dataLayerText]);

  const redirectToOldPanel = () => {
    Cookies.set('AVISOS_OLD_PANEL', 1, {
      expires: 1
    });
    window.location.href = window.location.origin.concat('/panel.bum');
  };

  return (
    <ContentLayout isNeutral={isNeutral} isWarning={isWarning} isMobile={isMobile} fontSize={fontSize}>
      <div className='labelContainer'>
        <div className='iconContainer' data-panel='panel-modal-alertCircle'>
          <AlertCircle isNeutral={isNeutral} isWarning={isWarning} isMobile={isMobile} />
        </div>
        <p data-panel='panel-modal-alertMessage'>{children}</p>
      </div>
      <div className='buttonsContainer'>
        {optionalButtonText && (
          <Button
            data-panel={optionalButtonDataPanel || ''}
            isOptional
            isNeutral={isNeutral}
            isWarning={isWarning}
            onClick={() => {
              if (optionalButtonDataLayer) {
                sendCustomTrackingInfo(optionalButtonDataLayer);
              } else {
                sendCustomTrackingInfo({
                  event: 'trackEvent',
                  eventCategory: 'Panel',
                  eventAction: 'Mis avisos',
                  eventLabel: `Mensaje de advertencia-${dataLayerText}_Ignorar`
                });
              }
              onOptionalButtonPress();
            }}
            isMobile={isMobile}
          >
            {optionalButtonText}
          </Button>
        )}
        {buttonText && (
          <Button
            data-panel={buttonDataPanel || ''}
            isNeutral={isNeutral}
            isWarning={isWarning}
            position={setPosition}
            top={setTop}
            left={setLeft}
            onClick={() => {
              if (dataLayerText) {
                sendCustomTrackingInfo({
                  event: 'trackEvent',
                  eventCategory: 'Panel',
                  eventAction: 'Mis avisos',
                  eventLabel: `Mensaje de advertencia-${dataLayerText}_Ver aviso`
                });
              }
              onClose();
            }}
          >
            {buttonText}
          </Button>
        )}
        {isFromCRM && (
          <Button data-panel='oldPanel-button' isNeutral={isNeutral} isWarning={isWarning} onClick={redirectToOldPanel}>
            {t('panel.avisos.alert.newPanel.cta')}
          </Button>
        )}
      </div>
    </ContentLayout>
  );
};

const ContentLayout = styled.div`
  font-size: ${({ fontSize }) => fontSize || '15.75px'};
  background-color: ${selectColor('alertRed')};
  height: fit-content;
  padding: 12px 12px 12px 8px;
  border-radius: 4px;
  border-left: 5px solid ${selectColor('feedbackWrong')};
  color: ${selectColor('feedbackWrong')};
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  .labelContainer {
    align-items: center;
    display: flex;
    white-space: normal;
  }
  .buttonsContainer {
    display: flex;
    justify-content: flex-end;
  }
  ${({ isNeutral }) =>
    isNeutral &&
    css`
      background-color: ${selectColor('grayLight')};
      border-left: 5px solid ${selectColor('blue')};
      color: ${selectColor('black')};
    `}
  ${({ isWarning }) =>
    isWarning &&
    css`
      background-color: ${select('lightYellow')};
      border-left: 5px solid ${selectColor('brownYellow')};
      color: ${selectColor('black')};
      justify-content: flex-start;
      gap: 4px;
    `}
  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;
      p {
        max-width: 93%;
      }
      .labelContainer {
        align-items: baseline;
      }
      .iconContainer {
        position: relative;
        top: 6px;
      }
    `}
`;

const AlertCircle = styled.div`
  background-image: url(${alertCircleIcon});
  width: 16px;
  height: 16px;
  margin-right: 5px;
  margin-bottom: 3px;
  ${({ isNeutral }) =>
    isNeutral &&
    css`
      background-image: url(${blueAlertCircleIcon});
      background-size: cover;
      margin-right: 9px;
    `};
  ${({ isWarning }) =>
    isWarning &&
    css`
      background-image: url(${brownAlertCircleIcon});
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      margin-right: 9px;
    `};
`;

const Button = styled.div`
  cursor: pointer;
  font-weight: 500;
  margin-right: 14px;
  position: ${({ position }) => position || 'static'};
  top: ${({ top }) => top || ''};
  left: ${({ left }) => left || ''};
    ${({ isNeutral }) =>
      isNeutral &&
      css`
        color: ${selectColor('blue')};
      `}
    ${({ isWarning }) =>
      isWarning &&
      css`
        color: ${selectColor('brownYellow')};
      `}
    ${({ isOptional, isMobile }) =>
      isOptional &&
      css`
        margin-right: ${isMobile ? 10 : 14}px;
      `};
`;

export default Alert;
